import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import Container from '../components/common/Container';
import {
  EnvelopeIcon,
  MapPinIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  AcademicCapIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

interface FormData {
  name: string;
  email: string;
  phone?: string;
  organization?: string;
  role: string;
  message: string;
}

const Contact: React.FC = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    console.log('Form data:', data);
    setIsSubmitting(false);
    setSubmitSuccess(true);
    reset();
    setTimeout(() => setSubmitSuccess(false), 5000);
  };

  const contactMethods = [
    {
      icon: EnvelopeIcon,
      title: 'Email Us',
      description: 'Get in touch anytime',
      value: 'hello@lumen8.it',
      href: 'mailto:hello@lumen8.it',
      gradient: 'from-[var(--color-electric-blue)] to-[var(--color-bright-cyan)]',
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: 'Book a Demo',
      description: 'See Lumen8 in action',
      value: 'Schedule a call',
      href: '#contact-form',
      gradient: 'from-[var(--color-violet)] to-[var(--color-electric-blue)]',
    },
    {
      icon: MapPinIcon,
      title: 'Location',
      description: 'Based in Australia',
      value: 'Sydney, NSW',
      href: null,
      gradient: 'from-[var(--color-emerald)] to-[var(--color-bright-cyan)]',
    },
  ];

  const faqs = [
    {
      icon: AcademicCapIcon,
      question: 'How do I get started with Lumen8?',
      answer: 'Contact us to schedule a demo and discuss how Lumen8 can work for your school or family. We\'ll work with you to find the right solution for your specific needs.',
    },
    {
      icon: SparklesIcon,
      question: 'Is Lumen8 aligned with the Australian curriculum?',
      answer: 'Yes! Lumen8 is fully mapped to Australian curriculum outcomes from Kindergarten to Year 12, covering multiple subjects in one unified platform.',
    },
    {
      icon: BuildingOfficeIcon,
      question: 'How long does implementation take?',
      answer: 'Most schools are up and running within 2 weeks. We provide full onboarding support, teacher training, and ongoing assistance to ensure a smooth transition.',
    },
    {
      icon: UserGroupIcon,
      question: 'Can parents use Lumen8 independently?',
      answer: 'Absolutely! Lumen8 works for both school-integrated use and independent home learning. Parents can track progress and support their child\'s learning journey.',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-[var(--color-midnight)] to-[var(--color-dark-navy)] overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[var(--color-electric-blue)] rounded-full filter blur-[128px] opacity-20 float-animation"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[var(--color-violet)] rounded-full filter blur-[128px] opacity-20 float-animation" style={{ animationDelay: '2s' }}></div>
        </div>

        <Container size="xl" className="relative z-10">
          <motion.div
            ref={heroRef}
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={heroInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-block mb-6"
            >
              <div className="glass-card px-4 py-2 rounded-full border border-[var(--color-electric-blue)]/30">
                <span className="text-sm font-semibold text-[var(--color-electric-blue)]">
                  Let's Connect
                </span>
              </div>
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              <span className="text-white">Get in</span>
              <span className="text-gradient"> Touch</span>
            </h1>
            <p className="text-xl text-[var(--color-light-gray)] leading-relaxed">
              Ready to transform education? Whether you're a school, parent, or just curious about adaptive learning, we'd love to hear from you.
            </p>
          </motion.div>
        </Container>
      </section>

      {/* Contact Methods */}
      <section className="py-12 bg-[var(--color-dark-navy)]">
        <Container>
          <div className="grid md:grid-cols-3 gap-6">
            {contactMethods.map((method, index) => (
              <motion.div
                key={method.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {method.href ? (
                  <a href={method.href} className="block h-full">
                    <div className="card-premium p-6 h-full hover-glow transition-all duration-300 hover:scale-[1.02] text-center">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${method.gradient} p-3 mx-auto mb-4`}>
                        <method.icon className="w-full h-full text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-1">{method.title}</h3>
                      <p className="text-sm text-[var(--color-medium-gray)] mb-2">{method.description}</p>
                      <p className="text-[var(--color-electric-blue)] font-semibold">{method.value}</p>
                    </div>
                  </a>
                ) : (
                  <div className="card-premium p-6 h-full text-center">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${method.gradient} p-3 mx-auto mb-4`}>
                      <method.icon className="w-full h-full text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">{method.title}</h3>
                    <p className="text-sm text-[var(--color-medium-gray)] mb-2">{method.description}</p>
                    <p className="text-[var(--color-light-gray)] font-semibold">{method.value}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-gradient-to-b from-[var(--color-dark-navy)] to-[var(--color-midnight)]">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
            {/* Left Side - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Let's Start a <span className="text-gradient">Conversation</span>
              </h2>
              <p className="text-lg text-[var(--color-light-gray)] mb-8">
                Fill out the form and our team will get back to you within 24 hours. Or reach out directly via email.
              </p>

              <div className="space-y-4">
                {[
                  'Personalised demo of Lumen8',
                  'Answers to your questions',
                  'Information on school partnerships',
                  'Home learning options for families',
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center gap-3"
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--color-emerald)]/20 flex items-center justify-center">
                      <CheckCircleIcon className="w-4 h-4 text-[var(--color-emerald)]" />
                    </div>
                    <span className="text-[var(--color-light-gray)]">{item}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-12 p-6 glass-card">
                <p className="text-[var(--color-light-gray)] italic">
                  "We're passionate about making quality education accessible to every student. Let's chat about how Lumen8 can help."
                </p>
                <p className="mt-4 text-white font-semibold">— The Lumen8 Team</p>
              </div>
            </motion.div>

            {/* Right Side - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="card-premium p-8">
                {submitSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 rounded-lg bg-[var(--color-emerald)]/20 border border-[var(--color-emerald)]/50"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircleIcon className="w-6 h-6 text-[var(--color-emerald)]" />
                      <p className="text-[var(--color-emerald)] font-semibold">
                        Thank you! We'll be in touch soon.
                      </p>
                    </div>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-light-gray)] mb-2">
                        Full Name *
                      </label>
                      <input
                        {...register('name', { required: 'Name is required' })}
                        type="text"
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-[var(--color-medium-gray)] focus:outline-none focus:ring-2 focus:ring-[var(--color-electric-blue)] focus:border-transparent transition-all"
                        placeholder="Your name"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-[var(--color-rose)]">{errors.name.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-light-gray)] mb-2">
                        Email Address *
                      </label>
                      <input
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                        })}
                        type="email"
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-[var(--color-medium-gray)] focus:outline-none focus:ring-2 focus:ring-[var(--color-electric-blue)] focus:border-transparent transition-all"
                        placeholder="you@example.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-[var(--color-rose)]">{errors.email.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[var(--color-light-gray)] mb-2">
                        Phone Number
                      </label>
                      <input
                        {...register('phone')}
                        type="tel"
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-[var(--color-medium-gray)] focus:outline-none focus:ring-2 focus:ring-[var(--color-electric-blue)] focus:border-transparent transition-all"
                        placeholder="Optional"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[var(--color-light-gray)] mb-2">
                        School / Organisation
                      </label>
                      <input
                        {...register('organization')}
                        type="text"
                        className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-[var(--color-medium-gray)] focus:outline-none focus:ring-2 focus:ring-[var(--color-electric-blue)] focus:border-transparent transition-all"
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-light-gray)] mb-2">
                      I am a... *
                    </label>
                    <select
                      {...register('role', { required: 'Please select your role' })}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[var(--color-electric-blue)] focus:border-transparent transition-all"
                    >
                      <option value="" className="bg-[var(--color-dark-navy)]">Select your role</option>
                      <option value="teacher" className="bg-[var(--color-dark-navy)]">Teacher</option>
                      <option value="parent" className="bg-[var(--color-dark-navy)]">Parent</option>
                      <option value="administrator" className="bg-[var(--color-dark-navy)]">School Administrator</option>
                      <option value="student" className="bg-[var(--color-dark-navy)]">Student</option>
                      <option value="investor" className="bg-[var(--color-dark-navy)]">Investor</option>
                      <option value="other" className="bg-[var(--color-dark-navy)]">Other</option>
                    </select>
                    {errors.role && (
                      <p className="mt-1 text-sm text-[var(--color-rose)]">{errors.role.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--color-light-gray)] mb-2">
                      Message *
                    </label>
                    <textarea
                      {...register('message', { required: 'Message is required' })}
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-[var(--color-medium-gray)] focus:outline-none focus:ring-2 focus:ring-[var(--color-electric-blue)] focus:border-transparent transition-all resize-none"
                      placeholder="Tell us about your needs..."
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-[var(--color-rose)]">{errors.message.message}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full btn-premium text-lg py-4 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <ArrowRightIcon className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[var(--color-midnight)]">
        <Container>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
              <p className="text-xl text-[var(--color-light-gray)]">
                Quick answers to common questions
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="card-premium p-6 h-full hover-glow">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[var(--color-electric-blue)]/20 flex items-center justify-center">
                      <faq.icon className="w-5 h-5 text-[var(--color-electric-blue)]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-[var(--color-light-gray)] text-sm leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
};

export default Contact;
